import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  FormHelperText,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import httpclient from "../../../../Utils";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import { useNotification } from "../../../../Hooks/useNotification";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import { getStatusColorAndVariant } from "../../../../Utils/utils";

const AssignPickerModal = ({
  open,
  onClose,
  title,
  jobIds = [],
  pickers = [],
  onAssignSuccess,
}) => {
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedPicker, setSelectedPicker] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notifyPicker, setNotifyPicker] = useState(true);
  const [customMessage, setCustomMessage] = useState("");

  const handlePickerChange = (event, newValue) => {
    setSelectedPicker(newValue);
    if (newValue) {
      setError("");
    }
  };

  const handleAssign = async () => {
    if (!selectedPicker) {
      setError("Please select a picker.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      const payload = {
        picker: selectedPicker.picker_id,
        job_list: jobIds,
        order_alert_message: notifyPicker ? 1 : 0, // Add notify flag
      };
      if (notifyPicker) {
        payload.custom_message = customMessage;
      }

      const response = await httpclient.post(
        "/orders/assign-multiple-order",
        payload
      );

      if (response.data?.status === 200 || response.status === 200) {
        showNotification(
          "success",
          response.data?.message || "Orders assigned successfully!"
        );
        if (onAssignSuccess) {
          onAssignSuccess(); // Callback to parent
        }
      } else {
        showNotification(
          "error",
          response.data?.message || "Failed to assign orders."
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedPicker(null);
      setError("");
      setNotifyPicker(false); // Reset notify state
      setCustomMessage("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isSmallScreen}
      aria-labelledby="assign-picker-dialog-title"
      sx={{
        "& .MuiDialogContent-root": {
          pt: 1,
        },
      }}
      PaperProps={{
        sx: {
          minHeight: "450px",
        },
      }}
    >
      <DialogTitle id="assign-picker-dialog-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Selected {jobIds.length === 1 ? "Order" : "Orders"}: {jobIds.length}
          </Typography>
          {jobIds.length > 0 ? (
            <Paper
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                p: 1,
                bgcolor: "#f5f5f5",
                border: "1px solid #ddd",
                borderRadius: 1,
              }}
              elevation={0}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {jobIds.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    size="small"
                    variant="outlined"
                    sx={{ m: 0.25 }}
                  />
                ))}
              </Box>
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No orders selected.
            </Typography>
          )}
        </Box>
        <FormControl fullWidth error={!!error} sx={{ mt: 2 }}>
          <Autocomplete
            disablePortal
            id="assign-picker-autocomplete"
            options={pickers}
            getOptionLabel={(option) => option.picker_name || ""}
            renderOption={(props, option) => {
              const { color, chipColor } = getStatusColorAndVariant(
                option.app_status_color
              );
              return (
                <Box
                  component="li"
                  {...props}
                  aria-label={`${option.picker_name}, status: ${option.app_status}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon
                      sx={{ color: chipColor, fontSize: 20, flexShrink: 0 }}
                      aria-hidden="true"
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.picker_name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#667085" }}>
                    -
                  </Typography>
                  <Chip
                    label={option.app_status}
                    size="small"
                    color={color}
                    sx={{
                      flexShrink: 0,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>
              );
            }}
            value={selectedPicker}
            onChange={handlePickerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Picker"
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Select a picker",
                }}
              />
            )}
            disabled={isSubmitting || jobIds.length === 0}
            isOptionEqualToValue={(option, value) =>
              option.picker_id === value.picker_id
            }
            ListboxComponent="div"
          />
          {error && <FormHelperText>{error}</FormHelperText>}
          {selectedPicker && selectedPicker.app_status !== "Online" && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1,
                backgroundColor:
                  selectedPicker.app_status === "Offline"
                    ? "#fdf8f1ff"
                    : "#fcf4f4ff",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
              }}
            >
              {/* Triangular tip/arrow */}
              <Box
                sx={{
                  position: "absolute",
                  top: -8,
                  left: 16,
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: (theme) => `8px solid ${theme.palette.divider}`,
                }}
              />
              {/* Another layer for the inner triangle to match background color */}
              <Box
                sx={{
                  position: "absolute",
                  top: -7,
                  left: 16,
                  width: 0,
                  height: 0,
                  borderLeft: "7px solid transparent",
                  borderRight: "7px solid transparent",
                  borderBottom: (theme) =>
                    `7px solid ${theme.palette.grey[100]}`,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color:
                    selectedPicker.app_status === "Offline"
                      ? "#e65100"
                      : "#c62828",
                  flex: 1,
                }}
              >
                Please note that {selectedPicker.picker_name} is{" "}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    display: "inline",
                  }}
                >
                  {selectedPicker.app_status}
                </Typography>
                .
              </Typography>
            </Box>
          )}
        </FormControl>

        {/* Notify Picker Checkbox - appears when picker is selected */}
        {selectedPicker && (
          <>
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyPicker}
                    onChange={(e) => setNotifyPicker(e.target.checked)}
                    size="small"
                    sx={{ py: 0.5 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 400, color: "#667085" }}
                  >
                    Notify selected picker
                  </Typography>
                }
                sx={{
                  ml: -1,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Custom Message"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                inputProps={{
                  maxLength: 200,
                }}
                placeholder="Enter a custom message for the picker (max 200 characters)"
                autoFocus
              />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {customMessage.length}/200 characters
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={isSubmitting || jobIds.length === 0 || !selectedPicker}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? "Assigning..." : "Assign Picker"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPickerModal;
